import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  clearLifecycleIdempotencyKey,
  lifecycleIdempotencyKey,
  requireExactGuestUpgradeResponse,
  shouldClearLifecycleIdempotencyKey,
} from '../lib/lifecycleIdempotency';

const DISMISS_KEY = 'batchtrue.guest-banner-dismissed';

export default function GuestUpgradeBanner() {
  const { user, updateUser } = useAuth();
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  // Dismissal persists across route changes for this browser session only.
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* storage unavailable */ }
  };

  // Only show for guest users
  if (!user || user.status !== 'guest' || dismissed) {
    return null;
  }

  const handleUpgrade = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      toast.error('Please enter your invite code');
      return;
    }

    setLoading(true);
    const payload = { invite_code: inviteCode.trim().toUpperCase() };
    const routeKind = 'guest_upgrade';
    const actorId = user.id;
    try {
      const idempotencyKey = lifecycleIdempotencyKey({ routeKind, actorId, payload });
      const res = await api.post('/auth/upgrade-guest', 
        payload,
        { headers: {
          'Idempotency-Key': idempotencyKey,
        } }
      );
      requireExactGuestUpgradeResponse(res.data, actorId);
      clearLifecycleIdempotencyKey({ routeKind, actorId });
      // Update user in context and localStorage
      if (res.data.user) {
        updateUser(res.data.user);
      }
      
      toast.success(res.data.user.status === 'approved' ? 'Your account is approved. You can now place orders.' : 'Account upgraded! Pending admin approval to unlock ordering.');
      setDismissed(true);
    } catch (err) {
      if (shouldClearLifecycleIdempotencyKey(err)) {
        clearLifecycleIdempotencyKey({ routeKind, actorId });
      }
      const errorMsg = err.response?.data?.detail || 'Failed to upgrade account';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-y border-[hsl(var(--warning)/0.4)] border-l-[3px] border-l-[hsl(var(--warning))] bg-[hsl(var(--warning)/0.1)]" data-testid="guest-upgrade-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex w-full min-w-0 items-start gap-3 sm:flex-1 sm:items-center">
            <Shield className="h-5 w-5 text-[hsl(var(--warning))] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">
                Guest account: catalog and pricing
              </p>
              <p className="text-xs text-muted-foreground">
                Build a cart now. Membership approval is required to place an order.
              </p>
            </div>
          </div>
          
          <form onSubmit={handleUpgrade} className="flex w-full items-center gap-2 sm:w-auto sm:shrink-0">
            <Input
              type="text"
              aria-label="Invite code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="MEMBERSHIP INVITE"
              className="h-10 min-w-0 flex-1 bg-card text-xs font-mono border-border sm:h-9 sm:w-44 sm:flex-none"
              data-testid="banner-invite-code-input"
              disabled={loading}
            />
            <Button 
              type="submit" 
              size="sm" 
              className="h-10 bg-primary hover:bg-[hsl(var(--primary-hover))] text-primary-foreground sm:h-9"
              disabled={loading}
              data-testid="banner-upgrade-button"
            >
              {loading ? 'Submitting...' : 'Request access'}
            </Button>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss"
              className="p-1 rounded transition-colors hover:bg-[hsl(var(--warning)/0.18)]"
              data-testid="banner-dismiss-button"
            >
              <X className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
